<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { signIn } from '@/services/authSignin'
import { useDisplay } from 'vuetify'
import router from '@/router'
import Cookies from 'universal-cookie'
import { fetchAllUsers, VerifyUsers, sendLogInfo } from '@/api/accountsClient'
import ProgressLoader from '@/components/ProgressLoader.vue'
import Menu from '@/components/Menu.vue'
import Hero from '@/components/Hero.vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import { useToast } from 'primevue/usetoast'
import NewPasswordDialog from '@/components/NewPasswordDialog.vue'
import { useNewPasswordDialog } from '@/composables/useNewPasswordDialog'

import * as UAParser from 'ua-parser-js'

async function buildUserContext(loginMethod = 'Cognito') {
  const parser = new UAParser.UAParser(window.navigator.userAgent)
  const ua = parser.getResult()

  const deviceInfo = `${ua.browser.name} ${ua.browser.version} on ${ua.os.name} ${ua.os.version}`

  let ipAddress = ''
  try {
    const res = await fetch('https://api.ipify.org?format=json')
    const data = await res.json()
    ipAddress = data.ip
  } catch (err) {
    console.warn('Could not fetch IP:', err)
  }
  // console.log(loginMethod, '==', window.navigator.userAgent, '===', ipAddress, '==.', deviceInfo)
  loginInfo.value = {
    loginMethod: loginMethod,
    userAgent: window.navigator.userAgent,
    ipAddress: ipAddress,
    deviceInfo: deviceInfo,
  }
}

export interface UserContext {
  loginMethod: string
  userAgent: string
  ipAddress: string
  deviceInfo: string
}

const loginInfo = ref<UserContext>({
  loginMethod: '',
  userAgent: '',
  ipAddress: '',
  deviceInfo: '',
})

const toast = useToast()
// import Button from 'primevue/button';
// import InputText from 'primevue/inputtext';
import Password from 'primevue/password'
import Checkbox from 'primevue/checkbox'

const showDemo = ref(false)
const demoEmail = ref('')
const demoPhone = ref('')
const demoMessage = ref('')
function submitDemo() {
  console.log('Demo Request:', demoEmail.value, demoPhone.value, demoMessage.value)
  showDemo.value = false
}

const rememberMe = ref(false)
const cookei = new Cookies()
const { xs } = useDisplay() // Responsive adjustments

const username = ref('')
const juid1 = ref('')

const password = ref('')
const showPassword = ref(false)

const message = ref('')
const snackbar = ref(false)
import { useUserStore } from '@/stores/userStore'
import axios from 'axios'
import { axiosInstance } from '@/api'
const isLoading = ref(false)

const tokenLoader = ref(false)

const userStore = useUserStore()

// New password dialog
const { isDialogOpen, submitNewPassword, cancelDialog } = useNewPasswordDialog()
const passwordDialogRef = ref()

async function handlePasswordSubmit(newPassword: string) {
  try {
    passwordDialogRef.value?.setLoading(true)
    await submitNewPassword(newPassword)
    // Success is handled by the signIn callback
  } catch (error: any) {
    console.error('Error setting password:', error)
    passwordDialogRef.value?.setError(error.message || 'Failed to set password. Please try again.')
  }
}

const handleSignIn = async () => {
  isLoading.value = true
  try {
    if (!username.value || !password.value) {
      toast.add({
        summary: 'Error Message',
        detail: 'Please fill all fields',
        life: 5000,
        styleClass: 'my-toast',
      })

      return
    }
    isLoading.value = true

    const response = await signIn(username.value, password.value)
    if (response) {
      sendLogInfo(loginInfo.value)
    }

    const role = response?.accessToken?.payload?.['cognito:groups'][0]
    const idToken = response?.idToken?.jwtToken

    const juid = response?.idToken?.payload?.['custom:juid']

    const urlParams = new URLSearchParams(window.location.search)
    const redirect = urlParams.get('redirect')
    const redirectdomain = urlParams.get('redirectdomain')

    if (redirect) {
      const user = cookei.get('user')
      const hasQueryParams = redirectdomain.includes('?')
      console.log('hello', hasQueryParams)
      if (!hasQueryParams) {
        window.location.href = `${redirectdomain}?token=${idToken}&role=${role}&juid=${juid}&redirectionDo=true`
      } else if (hasQueryParams) {
        window.location.href = `${redirectdomain}&token=${idToken}&role=${role}&juid=${juid}&redirectionDo=true`
      }
    } else if (juid) {
      router.push(`/account/${juid}`)
    }

    message.value = '✅ Sign-in successful!'
    snackbar.value = true
    // console.log("User response:", response);
    isLoading.value = false
  } catch (error: any) {
    console.log(error)
    message.value = error.message || '❌ Sign-in failed.'
    snackbar.value = true
    isLoading.value = false
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  buildUserContext()

  const urlParams = new URLSearchParams(window.location.search)
  const redirect = urlParams.get('redirect')
  const redirectdomain = urlParams.get('redirectdomain')

  if (redirect) {
    try {
      const hasQueryParams = redirectdomain.includes('?')
      console.log('hello', hasQueryParams)
      tokenLoader.value = true
      const data = await VerifyUsers()
      console.log(data)

      if (!hasQueryParams && data) {
        window.location.href = `${redirectdomain}?token=${userStore?.token}&role=${userStore?.role}&juid=${userStore?.juid}&redirectionDo=true`
      } else if (hasQueryParams && data) {
        window.location.href = `${redirectdomain}&token=${userStore?.token}&role=${userStore?.role}&juid=${userStore?.juid}&redirectionDo=true`
      }

      tokenLoader.value = false
    } catch (error) {
      // alert('Enter your credentials')
      tokenLoader.value = false
    }
  } else if (!redirect) {
    try {
      if (userStore?.token || userStore?.token) {
        if (
          userStore?.role === 'company_user' ||
          userStore?.role === 'company_admin' ||
          userStore?.role === 'company_suadmin' ||
          userStore?.role === 'company_owner'
        ) {
          router.push('/account-admin')
        } else if (userStore.juid) {
          tokenLoader.value = true
          try {
            const data = await VerifyUsers()
            if (data) {
              tokenLoader.value = false

              router.push(`/account/${userStore.juid}`)
              tokenLoader.value = false
            }
            tokenLoader.value = false
          } catch (error) {
            tokenLoader.value = false
          }
        }
      }
    } catch (error) {
      tokenLoader.value = false
    }
  }
})
// Validation rules

const required = (v: string) => !!v || 'This field is required'
</script>
<template>
  <div class="hero-wrapper" id="hero">
    <!-- Left side: Hero Content -->
    <div class="hero-left">
      <span class="tag">Automotive Inventory Tools</span>
      <h1 class="hero-title">Smart Merchandising Tools for Automotive Inventory</h1>
      <p class="hero-subtitle">
        Whether you're a dealership team, an agency partner, or a web developer—SodaSoft.io helps
        you manage, surface, and promote inventory with modern, web-based tools that do the heavy
        lifting.
      </p>
      <div class="hero-buttons">
        <Button id="btn-request-demo" label="Log in into your account" />
        <Button id="secondary-btn" @click="showDemo = true" label="Request a demo" />
      </div>
    </div>

    <!-- Right side: Login Form -->
    <div class="hero-right" id="login">
      <div class="text-start mb-6">
        <h2 class="form-title">Log in to your account</h2>
        <p class="form-subtitle">Enter your credentials to access your SodaSoft.io dashboard</p>
      </div>

      <v-form @submit.prevent="handleSignIn" class="d-flex flex-column gap-4">
        <div class="input-div">
          <div style="display: flex; flex-direction: column; gap: 0px">
            <span class="labels">Username</span>
            <input type="text" placeholder="email@jazel.com" v-model="username" class="input" />
          </div>

          <div style="display: flex; flex-direction: column; gap: 0px; margin-top: 15px">
            <div style="display: flex; justify-content: space-between">
              <span class="labels">Password</span>
              <span class="link" @click="router.push('/reset-password')">
                Forgot&nbsp;Password?
              </span>
            </div>
            <div class="password-wrapper">
              <input :type="showPassword ? 'text' : 'password'" v-model="password" class="input" />
              <button type="button" class="eye-btn" @click="showPassword = !showPassword">
                <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- <div>
          <checkbox  />
        </div> -->

        <v-btn
          block
          color="#2a68ed"
          :disabled="isLoading"
          type="submit"
          height="42"
          class="login-btn mt-4"
        >
          Sign in
        </v-btn>

        <p class="fineprint">
          Don't have an account?
          <span style="color: #2a68ed; cursor: pointer" @click="showDemo = true">
            Request a demo
          </span>
          .
        </p>

        <div class="separator"><span>OR</span></div>
        <p class="fineprint">Are you a SodaSoft Agent or Admin?</p>

        <Button
          @click="router.push('/company/login')"
          id="secondary-btn-1"
          label="Log in into Company Portal"
        />
      </v-form>
    </div>
    <Dialog
      v-model:visible="showDemo"
      modal
      style="width: 90%; max-width: 400px"
      class="demo-dialog"
    >
      <template #header>
        <div style="padding: 1rem 20px; font-weight: 600; font-size: 1.25rem">Request a Demo</div>
      </template>
      <div class="p-fluid">
        <div class="field">
          <label for="demo-email">Email</label>
          <InputText id="demo-email" v-model="demoEmail" type="email" required />
        </div>
        <div class="field mt-2">
          <label for="demo-phone">Contact No</label>
          <InputText id="demo-phone" v-model="demoPhone" type="text" />
        </div>
        <div class="field mt-2">
          <label for="demo-message">Message</label>
          <Textarea id="demo-message" v-model="demoMessage" rows="4" />
        </div>
        <div class="flex justify-content-end mt-3">
          <Button id="btn-request-demo" icon="pi pi-check" label="Submit" @click="submitDemo" />
        </div>
      </div>
    </Dialog>
    <v-snackbar v-model="snackbar" color="warning" :timeout="3000">
      {{ message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- New Password Dialog -->
    <NewPasswordDialog
      ref="passwordDialogRef"
      v-model="isDialogOpen"
      @confirm="handlePasswordSubmit"
      @cancel="cancelDialog"
    />
  </div>

</template>

<style scoped>
* {
  font-family: 'Geist', sans-serif;
  font-optical-sizing: auto;

  font-style: normal;
}

.my-toast {
  border-radius: 8px;
  font-size: 14px;
}

.p-toast-detail {
  /* background-color: red !important; */
  padding: 5px;
}

.input::placeholder {
  font-size: 14px;
}

.p-fluid {
  padding: 20px;
}

.password-wrapper {
  position: relative;
  width: 100%;
}

.separator {
  text-align: center;
  font-size: 12px;
  color: rgb(100, 116, 139);
}

.login-btn {
  box-shadow: none;
  font-size: 14px;
  text-transform: capitalize;
  font-family: 'Geist', sans-serif;
  letter-spacing: 0px;
}

.eye-btn {
  position: absolute;
  top: 50%;
  right: 0.8em;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
}

#btn-request-demo {
  background-color: #2a68ed !important;
  color: white;
  padding: 10px 29px;
  border-radius: 6px;
  font-size: 14px;
}

#btn-request-demo:hover {
  background-color: #3874f6 !important;
}

.labels {
  color: rgb(15, 23, 41);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
}

.input {
  border: 1px solid rgb(225, 225, 225);
  border-radius: 8px;
  height: 40px;
  padding: 0px 15px;
  width: 100%;
  background-color: white !important;
}

.hero-banner {
  padding: 4rem 1rem;
  font-family: 'Geist', sans-serif;
}

.container.grid {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 3rem;
  max-width: 1440px;
  margin: auto;
  flex-wrap: wrap;
}

.hero-left {
  flex: 1 1 55%;
  min-width: 300px;
}

.hero-right {
  flex: 1 1 38%;
  padding: 1.7rem 1.6rem;
  border-radius: 10px;
  /* box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05); */
  box-shadow: none;
  min-width: 280px;
  max-width: 450px;
  border: 1px solid rgb(241, 240, 240);
}

.tag {
  display: inline-block;
  background: #e6efff;
  color: #2463eb;
  padding: 0.3rem 0.85rem;
  font-size: 0.75rem;
  font-weight: 700 !important;
  border-radius: 20px;
  margin-bottom: 1rem;
}

h1 {
  font-size: 2.5rem;
  font-weight: 800;
  color: #0b0b0b;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1rem;
  color: #666;
  margin-bottom: 2rem;
  max-width: 600px;
}

.hero-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.primary-btn {
  background-color: #215cdc;
  border: none;
  color: white;
  padding: 0.7rem 1.5rem;
  font-size: 0.9rem;
  border-radius: 8px;
}

.primary-btn:hover {
  background-color: #2a69ef;
}

#secondary-btn {
  background-color: rgb(255, 255, 255) !important;
  color: #000000;
  border: 1px solid #e9e9e9;
  padding: 0.7rem 1.5rem;
  font-size: 0.9rem;
  border-radius: 8px;
}

#secondary-btn-1 {
  background-color: rgb(255, 255, 255) !important;
  color: #000000;
  border: 1px solid #e9e9e9;
  padding: 0.7rem 1.5rem;
  font-size: 0.9rem;
  border-radius: 8px;
}

#secondary-btn:hover {
  background-color: #f2f2f2 !important;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 0.2rem;
  color: rgb(15, 23, 41);
  letter-spacing: -0.5px;
}

.form-subtitle,
.fineprint {
  color: rgb(100, 116, 139);
  font-size: 14px;
  margin-bottom: 1rem;
}

.fineprint {
  margin-top: 20px;
  text-align: center;
}

.form-footer {
  font-size: 0.85rem;
  margin: 1.2rem 0;
  color: #444;
}

.form-footer a {
  color: #215cdc;
  text-decoration: none;
}

.divider {
  text-align: center;
  margin: 1.5rem 0;
  font-size: 0.8rem;
  color: #999;
}

.link {
  font-size: 0.8rem;
  color: rgb(100, 116, 139);
  text-decoration: none;
  cursor: pointer;
}

.link:hover {
  color: rgb(15, 23, 41);
}

@media (max-width: 768px) {
  .container.grid {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-left,
  .hero-right {
    width: 100%;
  }

  h1 {
    font-size: 2rem;
  }
}

.hero-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  padding: 3rem 0rem;
  max-width: 1340px;

  width: 100%;
  margin: 0 auto;
  gap: 2rem;
}

.hero-left {
  flex: 1 1 55%;
  min-width: 280px;
  margin-top: 45px;
}

.hero-right {
  flex: 1 1 40%;
  min-width: 280px;
}

.tag {
  background-color: #e7f1ff;
  color: #215cdc;
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 20px;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 1rem;
}

.hero-title {
  font-size: 3.7rem;
  font-weight: 700;
  color: #1c2739;
  margin-bottom: 1rem;
  line-height: 60px;
  letter-spacing: -2px;
  max-width: 700px;
}

.hero-subtitle {
  font-size: 1.2rem;
  color: rgb(100, 116, 139);
  line-height: 1.4;
  margin-bottom: 2rem;
  max-width: 520px;
}

.hero-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.primary-btn {
  background-color: #2a68ed !important;
  color: white;
  padding: 7px 13px;
  border-radius: 6px;
  font-size: 14px;
}

/* Input + Textarea Styling */
.p-inputtext,
.p-inputtextarea {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #0f172a;
  background-color: white;
  box-shadow: none;
  margin-top: 5px;
}

.p-inputtext::placeholder,
.p-inputtextarea::placeholder {
  color: #94a3b8;
}

.p-inputtext:focus,
.p-inputtextarea:focus {
  border-color: #2563eb;
  outline: none;
  box-shadow: 0 0 0 1px #2563eb;
}

@media (max-width: 768px) {
  .hero-wrapper {
    padding: 0px 30px;
  }

  .hero-title {
    font-size: 2rem;
    line-height: 50px;
  }
}

@media (max-width: 1380px) {
  .hero-wrapper {
    padding: 3rem 30px;
  }
}
</style>
